<i18n lang="yaml" locale="en">
  add in new box: Add in new box
  add child: "Add <b>{title}</b>"
</i18n>

<i18n lang="yaml" locale="de">
  add in new box: In neuer Box hinzufügen
  add child: "<b>{title}</b> hinzufügen"
</i18n>

<template>
  <transition name="fade">
    <div
      v-if="!$localeMode"
      class="editor"
      :class="{adding:addChildType}"
      @mousedown="addGridChild"
    >
      <dropzone
        ref="dropzone"
        :target="$parent"
        @drop="onDrop"
        :validation-fn="validateDrag"
      >
        {{ $t('add in new box') }}
      </dropzone>
      <div
        class="add-wrapper"
      >
        <div class="add-icon-container">
          <add-circle
            color="white"
            @click="showChildTypeDropdown = !showChildTypeDropdown"
            :active="showChildTypeDropdown"
            size="xl"
          />
        </div>
        <transition name="bounceLeft">
          <ul
            class="child-type-dropdown"
            v-if="showChildTypeDropdown"
          >
            <li
              class="child-type"
              v-for="(title, type) in $parent.$options.childOptions"
              @click.stop="setAddChildType(type)"
              :key="type"
            >
              <span v-html="$t('add child', { title })" />
            </li>
          </ul>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
import AddCircle from '@/components/icons/AddCircle'
import Dropzone from '@/components/editor/Dropzone'

export default {
  name: 'GridEditor',
  components: {
    AddCircle,
    Dropzone
  },
  data () {
    return {
      showChildTypeDropdown: false,
      addChildType: ''
    }
  },
  methods: {
    validateDrag (event) {
      if (event.dataTransfer.types.includes('assets')) {
        const paths = Object.keys(this.$root.$store.getters.selectedAssets)
        const accepted = paths.filter(path => {
          const module = this.$store.getters.modulesByPath[path]
          // only allow acceptedFileTypes
          return this.acceptedFileTypes.includes(module.getters.type.name)
        })
        return accepted.length > 0
      }
    },
    async onDrop (event) {
      if (event.dataTransfer.getData('assets')) {
        const assets = Object.keys(this.$root.$store.getters.selectedAssets).map(path => this.$store.getters.modulesByPath[path])
        this.addFileBox(event.offsetX, event.offsetY).then(child => {
          this.parseAssets(assets, child)
        })
      }
    },
    parseAssets (assets, parent) {
      for (const asset of assets) {
        if (asset.getters.type.name !== 'folder') {
          if (this.acceptedFileTypes.includes(asset.getters.type.name)) {
            parent.dispatch('cloneFrom', {
              path: asset.getters.path
            })
          }
        } else {
          this.parseAssets(asset.getters.children, parent)
        }
      }
      this.$root.$store.dispatch('deselectAllAssets')
    },
    async addFileBox (x, y) {
      const box = await this.addBox('box', x, y, 32, 16, true)
      return this.addChild(box, 'file')
    },
    addBox (boxType, x, y, w, h, centered = false) {
      const offsetX = centered ? w / 2 : 0
      const offsetY = centered ? h / 2 : 0
      return this.addChild(this.$store, boxType, {
        x: Math.min(this.$parent.width - w, Math.max(0, Math.round(x / this.$parent.boxWidth) - offsetX)),
        y: Math.min(this.$parent.height - h, Math.max(0, Math.round(y / this.$parent.boxHeight) - offsetY)),
        w: w,
        h: h
      })
    },
    addChild (parentStore, childType, props) {
      return parentStore.dispatch('addChild', {
        childType,
        props: props
      })
    },
    setAddChildType (childType) {
      this.showChildTypeDropdown = false
      this.addChildType = childType
    },
    async addGridChild (event) {
      if (this.addChildType) {
        this.$parent.$emit('editorMouseDown', true)
        const x = event.offsetX
        const y = event.offsetY
        const onMouseUp = () => {
          clearTimeout(clickTimer)
          this.addBox(this.addChildType, x, y, 32, 16, true)
          this.addChildType = ''
          this.$parent.$emit('editorMouseDown', false)
        }
        window.addEventListener('mouseup', onMouseUp, { once: true })
        const clickTimer = setTimeout(async () => {
          window.removeEventListener('mouseup', onMouseUp)
          const box = await this.addBox(this.addChildType, x, y, 4, 4)
          this.addChildType = ''
          this.$nextTick(() => {
            const vdr = box.$componentRef.$refs.editor.$refs.vdr
            vdr.deselect(event)
            vdr.handleDown('br', true)
            vdr.$once('resizestop', () => { this.$parent.$emit('editorMouseDown', false) })
          })
        }, 150)
      }
    }
  },
  computed: {
    acceptedFileTypes () {
      return ['image', 'video', 'pdf', 'otherFile', 'audio', 'folder']
    },
    $localeMode () {
      return this.$parent.$parent.$localeMode
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/uiColors';

.editor {
  height: 100%;
  width: 100%;

  &.adding {
    position: absolute;
    cursor: crosshair;
  }
}

.add-wrapper {
  position: fixed;
  bottom: 80px;
  left: 30px;
  font-size: 14px;
  line-height: 1.5em;
  letter-spacing: 0.5pt;

  .add-icon-container {
    background-color: $color-main;
    border-radius: 50%;
    padding: 3px;
  }

  .child-type-dropdown {
    width: 180px;
    padding: 0 10px;
    margin: 0;
    position: absolute;
    bottom: 0px;
    left: 100px;
    background-color: $color-main-darker;
    white-space: nowrap;

    &::before {
      content: '';
      position: absolute;
      left: -8px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
      width: 16px;
      height: 16px;
      background-color: $color-main-darker;
    }

    .child-type {
      overflow: hidden;
      padding: 8px 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.25);
      cursor: pointer;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        color: $color-main;
      }
    }
  }
}
</style>
